import { useEffect, useState, type ReactNode } from "react";
import { Link, Navigate, NavLink, Route, Routes, useLocation, useMatch } from "react-router";
import { BookOpen, GraduationCap, LayoutGrid, PanelLeft, PanelLeftClose, type LucideIcon } from "lucide-react";
import {
  AppLauncher,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Monogram,
  ProductMark,
  ThemeToggle,
} from "@codelittinc/carbon-design-system";
import type { Tool } from "@carbon-os/server/contract";
import { Confetti } from "./components/Confetti";
import { startAnalytics, trackApp, trackPageview } from "./lib/analytics";
import { useMyTools } from "./lib/api";
import { launcherSections } from "./lib/launcher";
import { startSession } from "./lib/session";
import { refuseUnknown } from "./lib/signin";
import { AuthPage } from "./routes/Auth";
import { ThemeSync } from "./lib/theme";
import { celebrate, overall, useTraining } from "./lib/training";
import { Handbook } from "./routes/Handbook";
import { Home } from "./routes/Home";
import { Training } from "./routes/Training";

export function App() {
  usePageviews();
  return <Routes>
    <Route path="/sign-in" element={<AuthPage mode="sign-in" />} />
    <Route path="/sign-out" element={<AuthPage mode="sign-out" />} />
    <Route path="/signed-out" element={<AuthPage mode="sign-out" />} />
    <Route path="*" element={<Workspace />} />
  </Routes>;
}

/**
 * A `$pageview` for each path the person goes to, once PostHog has started.
 * By the effect, the browser's address is already the new one; a query change
 * (a tab, a search, a folder) is the same page.
 */
function usePageviews() {
  const { pathname } = useLocation();
  useEffect(() => trackPageview(pathname), [pathname]);
}

function Workspace() {
  const { state, retry } = useMyTools();
  // Asked alongside the tool list, so Home's nudge and /training share one answer.
  const training = useTraining();
  // Items still to do, for the header badge; nothing while training loads or fails.
  const progress = training.state.kind === "ready" ? overall(training.state.tracks, training.state.done) : null;
  const left = progress ? progress.total - progress.done : 0;
  // A Handbook read acknowledges the process in Training too (Gatekeeper 1.23.0): done at once,
  // and a track it finishes celebrates. Training that failed to load is asked again instead.
  const handbookRead = (processId: string, readAt: string) => {
    if (training.state.kind !== "ready") return training.retry();
    for (const finished of training.acknowledged(processId, readAt)) celebrate(finished);
  };
  const onHandbook = useMatch({ path: "/handbook", end: false }) !== null;
  const page = useMatch("/training") !== null ? "Training" : onHandbook ? "Handbook" : "My tools";
  // No chrome for somebody Gatekeeper does not know: they are on their way out.
  const email =
    state.kind === "ready" && state.data.person
      ? state.data.signedInAs
      : state.kind === "wrong_account"
        ? state.signedInAs
        : null;

  // The tab names the page; index.html carries My tools' title for the first paint.
  useEffect(() => {
    document.title = `${page} · Carbon OS`;
  }, [page]);

  // From the first render, whatever the tool list says: the session cookie is
  // renewed in the background, so saving a minute later still works.
  useEffect(() => {
    void startSession();
  }, []);

  // Somebody Gatekeeper does not know cannot be signed in here: the server ends
  // their session and sends them to the sign-in page, which says why. They got
  // here signed in already (by another app), so /auth/session never saw them.
  const unknown = state.kind === "not_in_gatekeeper" || (state.kind === "ready" && !state.data.person);
  useEffect(() => {
    if (unknown) refuseUnknown();
  }, [unknown]);

  // Only for somebody in the staff directory: a refusal, an error or "we couldn't
  // find you" never loads PostHog.
  useEffect(() => {
    if (state.kind === "ready" && state.data.person) {
      startAnalytics(state.data.analytics, state.data.signedInAs);
    }
  }, [state]);

  return (
    <>
      <ThemeSync saved={state.kind === "ready" ? state.data.theme : undefined} />
      <Confetti />
      <Shell
        email={email}
        name={state.kind === "ready" ? (state.data.person?.name ?? null) : null}
        tools={state.kind === "ready" ? state.data.tools : null}
        page={page}
        left={left}
        busy={state.kind === "loading"}
      >
        {/* Somebody Gatekeeper does not know gets only the panel saying so, on
            every path: no Training, no Handbook, nothing else of the app. */}
        {unknown ? (
          <Home state={state} retry={retry} training={training.state} />
        ) : (
        /* The portfolio's old paths, and anything else, land on My tools. */
        <Routes>
          <Route path="/" element={<Home state={state} retry={retry} training={training.state} />} />
          <Route path="/training" element={<Training training={training} />} />
          {/* Loaded by the page itself, so every other page load stays at two requests. */}
          <Route path="/handbook/:documentId?" element={<Handbook onRead={handbookRead} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        )}
      </Shell>
    </>
  );
}

/** `end: false` keeps the entry marked on the pages under it (`/handbook/:id`). */
type NavEntry = { to: string; label: string; icon: LucideIcon; end?: boolean };

/** One section today; the Delinquency Center's grouped shape so the Carbon apps read alike. */
const NAV: { heading: string; items: NavEntry[] }[] = [
  {
    heading: "Workspace",
    items: [
      { to: "/", label: "My tools", icon: LayoutGrid },
      { to: "/training", label: "Training", icon: GraduationCap },
      { to: "/handbook", label: "Handbook", icon: BookOpen, end: false },
    ],
  },
];

const COLLAPSED_KEY = "carbon-os.sidebar.collapsed";

/** Persisted rail-or-full choice. Storage failing just means starting expanded. */
function useCollapsed(): [boolean, () => void] {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem(COLLAPSED_KEY) === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0");
    } catch {
      // Private mode: the toggle still works for this visit.
    }
  }, [collapsed]);
  return [collapsed, () => setCollapsed((c) => !c)];
}

/**
 * The Delinquency Center's frame: a fixed sidebar (brand, grouped nav, collapse
 * toggle) and a slim header naming the page, with the theme and the account at
 * the right. On a phone the sidebar is always the icon rail, in CSS alone; the
 * collapse toggle only matters from `sm` up.
 *
 * The chrome only once we know who is signed in: never while loading or signed
 * out. `main` stays at the same place in the tree either way, so the page under
 * it is not remounted when the chrome arrives (focus after a retry survives).
 */
function Shell({
  email,
  name,
  tools,
  page,
  left,
  busy,
  children,
}: {
  email: string | null;
  /** The person's name from the staff directory, for the avatar's initials. */
  name: string | null;
  /** Gatekeeper's apps for the switcher; null until they load, which hides it. */
  tools: Tool[] | null;
  page: string;
  left: number;
  busy: boolean;
  children: ReactNode;
}) {
  const [collapsed, toggle] = useCollapsed();
  const width = collapsed
    ? "w-[var(--sidebar-collapsed-width)]"
    : "w-[var(--sidebar-collapsed-width)] sm:w-[var(--sidebar-width)]";
  const inset = collapsed
    ? "left-[var(--sidebar-collapsed-width)]"
    : "left-[var(--sidebar-collapsed-width)] sm:left-[var(--sidebar-width)]";
  const pad = collapsed
    ? "pl-[var(--sidebar-collapsed-width)]"
    : "pl-[var(--sidebar-collapsed-width)] sm:pl-[var(--sidebar-width)]";

  return (
    <div className="min-h-screen bg-bg">
      {email && (
        <aside
          className={`fixed top-0 left-0 z-30 flex h-screen flex-col border-r border-border-subtle bg-surface transition-all duration-200 ${width}`}
        >
          <NavLink
            to="/"
            aria-label="Carbon OS home"
            className="flex h-[var(--header-height)] shrink-0 items-center gap-2 border-b border-border-subtle px-3.5"
          >
            {/* On a phone the rail is always compact, so the full mark only shows from `sm` up. */}
            {collapsed ? (
              <ProductMark name="Carbon OS" compact />
            ) : (
              <>
                <ProductMark name="Carbon OS" compact className="sm:hidden" />
                <ProductMark name="Carbon OS" className="hidden sm:inline-flex" />
              </>
            )}
          </NavLink>

          <nav aria-label="Main" className="flex-1 overflow-y-auto px-2 py-3">
            {NAV.map((group) => (
              <div key={group.heading} className="mb-4">
                <p
                  className={`${collapsed ? "hidden" : "hidden sm:block"} mb-1 px-2 text-[10px] font-medium tracking-widest text-text-faint uppercase`}
                >
                  {group.heading}
                </p>
                <div aria-hidden className={`${collapsed ? "" : "sm:hidden"} mx-auto mb-1 h-px w-6 bg-border`} />
                {group.items.map((item) => (
                  <NavItem key={item.to} item={item} collapsed={collapsed} count={item.to === "/training" ? left : 0} />
                ))}
              </div>
            ))}
          </nav>

          <Button
            variant="ghost"
            onClick={toggle}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="hidden h-10 w-full shrink-0 rounded-none border-t border-border-subtle text-text-muted hover:text-text-primary sm:flex"
          >
            {collapsed ? (
              <PanelLeft className="size-4" aria-hidden />
            ) : (
              <PanelLeftClose className="size-4" aria-hidden />
            )}
          </Button>
        </aside>
      )}

      {email && (
        <header
          className={`fixed top-0 right-0 z-20 flex h-[var(--header-height)] items-center border-b border-border-subtle bg-surface/80 backdrop-blur-md transition-all duration-200 ${inset}`}
        >
          <div className="flex w-full items-center justify-between gap-3 px-4 sm:px-6">
            <span className="min-w-0 truncate text-sm font-medium text-text-primary">{page}</span>
            <div className="flex shrink-0 items-center gap-2 sm:gap-3">
              {tools && <AppLauncher sections={launcherSections(tools)} className="h-11 w-11 sm:h-7 sm:w-7" />}
              <ThemeToggle className="h-11 w-11 sm:h-7 sm:w-7" />
              <AccountMenu email={email} name={name} />
            </div>
          </div>
        </header>
      )}

      <main
        id="main"
        aria-busy={busy}
        className={email ? `min-w-0 pt-[var(--header-height)] transition-all duration-200 ${pad}` : ""}
      >
        <div
          className={
            email ? "mx-auto max-w-6xl px-4 py-6 sm:px-8 sm:py-8" : "mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8"
          }
        >
          {children}
        </div>
      </main>
    </div>
  );
}

/**
 * A sidebar link. The current page is marked with `aria-current` (NavLink's
 * own) and the accent fill, so it never rests on colour alone. The label is
 * always in the accessible name, even when only the icon shows; on the rail the
 * count of training left becomes a dot on the icon.
 */
function NavItem({ item, collapsed, count }: { item: NavEntry; collapsed: boolean; count: number }) {
  const active = useMatch({ path: item.to, end: item.end ?? true }) !== null;
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end ?? true}
      title={item.label}
      {...(count > 0 ? { "aria-label": `${item.label}, ${count} left` } : {})}
      className={[
        "mb-0.5 flex min-h-11 items-center justify-center gap-2.5 rounded-md text-xs font-medium transition-colors sm:min-h-0 sm:py-1.5",
        collapsed ? "" : "sm:justify-start sm:px-2.5",
        active
          ? "bg-accent-muted text-accent-text"
          : "text-text-secondary hover:bg-surface-overlay hover:text-text-primary",
      ].join(" ")}
    >
      <span className="relative shrink-0">
        <Icon size={16} aria-hidden />
        {count > 0 && (
          <span
            aria-hidden
            className={`${collapsed ? "" : "sm:hidden"} absolute -top-1 -right-1 size-2 rounded-full bg-accent`}
          />
        )}
      </span>
      <span className={collapsed ? "sr-only" : "sr-only sm:not-sr-only sm:flex-1"}>{item.label}</span>
      {count > 0 && (
        <Badge variant="accent" className={collapsed ? "hidden" : "hidden sm:inline-flex"}>
          {count}
        </Badge>
      )}
    </NavLink>
  );
}

/** Account actions open the shared confirmation page before ending a session. */
function AccountMenu({ email, name }: { email: string; name: string | null }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-11 w-11 rounded-full p-0 hover:opacity-80 sm:h-8 sm:w-8"
          aria-label={`Account: ${email}`}
        >
          <Monogram name={name ?? email} size="sm" className="rounded-full" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-w-[calc(100vw-2rem)]">
        <DropdownMenuLabel>
          Signed in as
          <span className="block font-normal break-all text-text-primary">{email}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild className="min-h-11 sm:min-h-0">
          <Link to="/sign-out" onClick={() => trackApp("sign_out_clicked", { via: "sign_out" })}>
            Sign out
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="min-h-11 sm:min-h-0">
          <Link to="/sign-out" onClick={() => trackApp("sign_out_clicked", { via: "switch_account" })}>
            Sign in with a different account
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
